import { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';

import { PermissionRequirementPair, ResourcePermissionOverrideItemDTO } from '@vyce/core/src/api/types';
import { RolesModuleContext } from '@vyce/core/src/contexts/rolesModuleProvider';

import { NotificationContext } from '../../../contexts/notificationContext';
import {
  CatalogueIndex,
  closure,
  explicitFromEffective,
  setsEqual,
  toDisplayName,
} from '../utils/permissionState';
import { getPermissionErrorNotification, parsePermissionConflicts } from '../utils/permissionErrors';

const PAGE_LIMIT = 200;

interface Props {
  companyId?: string;
  userId: string;
  moduleSystemName: string;
  catalogue: CatalogueIndex | null;
  /** Load only once the module panel is actually open. */
  enabled: boolean;
}

export const useResourcePermissionExceptions = ({
  companyId,
  userId,
  moduleSystemName,
  catalogue,
  enabled,
}: Props) => {
  const { handleServerError, showNotification } = useContext(NotificationContext);
  const { getUserResourcePermissionOverridesRequest, updateResourcePermissionOverrideRequest } =
    useContext(RolesModuleContext);

  const [loading, setLoading] = useState(false);
  const [moduleDefault, setModuleDefault] = useState<string[]>([]);
  const [items, setItems] = useState<Record<string, ResourcePermissionOverrideItemDTO>>({});
  const [explicit, setExplicit] = useState<Record<string, string[]>>({});
  const [conflicts, setConflicts] = useState<Record<string, PermissionRequirementPair[]>>({});
  const [savingId, setSavingId] = useState<string | null>(null);
  const loadStartedRef = useRef(false);

  const resolveLabel = useCallback(
    (systemName: string) => (catalogue ? toDisplayName(systemName, catalogue.index) : systemName),
    [catalogue]
  );

  const load = useCallback(async () => {
    if (!companyId || !catalogue) return;
    try {
      setLoading(true);
      const collected: ResourcePermissionOverrideItemDTO[] = [];
      let offset = 0;
      let total = 0;
      let defaults: string[] = [];
      do {
        const res = await getUserResourcePermissionOverridesRequest(companyId, userId, {
          modules: [moduleSystemName],
          offset,
          limit: PAGE_LIMIT,
        });
        total = res.data?.total || 0;
        defaults = res.data?.module_defaults?.[moduleSystemName] || defaults;
        const page = res.data?.items || [];
        collected.push(...page);
        offset += PAGE_LIMIT;
        if (!page.length) break;
      } while (collected.length < total);

      const nextItems: Record<string, ResourcePermissionOverrideItemDTO> = {};
      const nextExplicit: Record<string, string[]> = {};
      collected
        .filter(item => item.module_system_name === moduleSystemName)
        .forEach(item => {
          nextItems[item.resource_id] = item;
          nextExplicit[item.resource_id] = Array.from(
            explicitFromEffective(item.effective || [], catalogue.index)
          );
        });

      setModuleDefault(defaults);
      setItems(nextItems);
      setExplicit(nextExplicit);
    } catch (e) {
      handleServerError(e);
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [companyId, userId, moduleSystemName, catalogue]);

  useEffect(() => {
    if (enabled && catalogue && !loadStartedRef.current) {
      loadStartedRef.current = true;
      load();
    }
  }, [enabled, catalogue, load]);

  const effectiveByResource = useMemo(() => {
    const result: Record<string, Set<string>> = {};
    if (!catalogue) return result;
    Object.keys(explicit).forEach(resourceId => {
      result[resourceId] = closure(explicit[resourceId] || [], catalogue.index);
    });
    return result;
  }, [explicit, catalogue]);

  const togglePermission = useCallback((resourceId: string, systemName: string, checked: boolean) => {
    setExplicit(prev => {
      const current = prev[resourceId] || [];
      return {
        ...prev,
        [resourceId]: checked
          ? Array.from(new Set([...current, systemName]))
          : current.filter(item => item !== systemName),
      };
    });
    setConflicts(prev => ({ ...prev, [resourceId]: [] }));
  }, []);

  /** Sending exactly the module default clears the exception server-side. */
  const resetResource = useCallback(
    (resourceId: string) => {
      if (!catalogue) return;
      setExplicit(prev => ({
        ...prev,
        [resourceId]: Array.from(explicitFromEffective(moduleDefault, catalogue.index)),
      }));
      setConflicts(prev => ({ ...prev, [resourceId]: [] }));
    },
    [catalogue, moduleDefault]
  );

  const isResourceDirty = useCallback(
    (resourceId: string) => {
      const baseline = items[resourceId]?.effective || [];
      const effective = effectiveByResource[resourceId];
      if (!effective) return false;
      return !setsEqual(effective, baseline);
    },
    [items, effectiveByResource]
  );

  const saveResource = useCallback(
    async (resourceId: string, orderedPermissionNames: string[]) => {
      if (!companyId) return;
      const effective = effectiveByResource[resourceId] || new Set<string>();
      try {
        setSavingId(resourceId);
        await updateResourcePermissionOverrideRequest(companyId, userId, resourceId, {
          permissions: orderedPermissionNames.filter(name => effective.has(name)),
        });
        setItems(prev => ({
          ...prev,
          [resourceId]: {
            ...(prev[resourceId] as ResourcePermissionOverrideItemDTO),
            effective: Array.from(effective),
            has_exception: !setsEqual(effective, moduleDefault),
          },
        }));
        setConflicts(prev => ({ ...prev, [resourceId]: [] }));
        showNotification({ message: 'Exception saved', options: { variant: 'success' } });
      } catch (e: any) {
        const notification = getPermissionErrorNotification(e, resolveLabel);
        if (notification) {
          showNotification(notification);
          if (e?.response?.status === 409) {
            setConflicts(prev => ({ ...prev, [resourceId]: parsePermissionConflicts(e) }));
          }
        } else {
          handleServerError(e);
        }
      } finally {
        setSavingId(null);
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [companyId, userId, effectiveByResource, moduleDefault, resolveLabel]
  );

  const hasAnyException = useMemo(() => Object.values(items).some(item => item.has_exception), [items]);

  return {
    loading,
    moduleDefault,
    items,
    explicit,
    conflicts,
    effectiveByResource,
    savingId,
    hasAnyException,
    togglePermission,
    resetResource,
    isResourceDirty,
    saveResource,
    reload: load,
  };
};
